// Public renderer — switches on section.kind and returns the correct markup.
import {
  Star,
  CheckCircle2, Youtube, Instagram, Phone, Mail, MapPin, ArrowRight, Play, MessageCircle,
} from "lucide-react";
import { ICONS, arr } from "./constants";
import { WaitlistFormSection, CountdownSection, RegistrationFormSection } from "./subcomponents";
import CustomCodeSection from "./CustomCodeSection";
import { renderableRichHtml } from "@/lib/richHtml";

// ---------- PUBLIC RENDERER ----------
export function SectionView({ section, accent = "#0B5CFF", funnelNext = null }) {
  const s = section || {};
  const ctaHref = funnelNext ? funnelNext.href : "#cta";
  const ctaClick = funnelNext ? funnelNext.onClick : undefined;
  switch (s.kind) {
    case "hero":
      return (
        <section className="vp-hero-bg">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-14 sm:py-20 lg:py-24 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7 vp-fade-up">
              <h1 data-inline-field="title" className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title || "Your headline") }} />
              {s.subtitle && <p data-inline-field="subtitle" className="text-[#4A4D57] text-base sm:text-lg mt-4 sm:mt-5 leading-relaxed max-w-xl" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.subtitle) }} />}
              {s.cta && <a data-inline-field="cta" href={ctaHref} onClick={ctaClick} className="vp-btn-primary text-sm sm:text-base mt-6 sm:mt-7 inline-block" style={{ background: accent, borderColor: accent }} dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.cta) }} />}
            </div>
            {s.image && (
              <div className="lg:col-span-5">
                <div className="vp-glass p-2">
                  <img src={s.image} alt="hero" className="w-full aspect-[4/5] object-cover rounded-xl" />
                </div>
              </div>
            )}
          </div>
        </section>
      );
    case "stats":
      return (
        <section className="py-12 sm:py-16 border-y border-[#E4E6EC] bg-[#F6F7FA]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 text-center">
            {arr(s.items).map((it, i) => (
              <div key={i}>
                <div className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight" style={{ color: accent }}>{it.n}</div>
                <div className="text-xs sm:text-sm text-[#4A4D57] mt-1">{it.l}</div>
              </div>
            ))}
          </div>
        </section>
      );
    case "features":
      return (
        <section className="py-16 sm:py-20">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight max-w-2xl leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {arr(s.items).map((it, i) => {
                const Icon = ICONS[it.icon] || null;
                return (
                  <div key={i} className="vp-card p-5">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: accent + "12", color: accent }}>
                      {Icon ? <Icon className="w-5 h-5" strokeWidth={1.75} /> : <span className="font-display font-black">{i + 1}</span>}
                    </div>
                    <div className="font-display font-semibold text-base sm:text-lg mt-3 leading-snug">{it.t}</div>
                    <p className="text-sm text-[#4A4D57] mt-1.5 leading-relaxed">{it.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      );
    case "testimonials":
      return (
        <section className="py-16 sm:py-20 bg-[#F6F7FA] border-y border-[#E4E6EC]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {arr(s.items).map((it, i) => (
              <div key={i} className="vp-card p-5 sm:p-6">
                <div className="flex text-[#F5A623]">{[0,1,2,3,4].map((k) => <Star key={k} className="w-4 h-4" fill="currentColor" strokeWidth={0} />)}</div>
                <p className="text-[#0B0D17] mt-3 font-display text-base sm:text-lg leading-snug">"{it.q}"</p>
                <div className="mt-3 text-xs">
                  <div className="font-semibold text-[#0B0D17]">{it.n}</div>
                  <div className="text-[#747680]">{it.r}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      );
    case "cta":
      return (
        <section id="cta" className="py-20" style={{ background: accent, color: "white" }}>
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <h2 data-inline-field="title" className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />
            {s.subtitle && <p data-inline-field="subtitle" className="opacity-90 text-lg mt-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.subtitle) }} />}
            {s.button && (
              <a
                href={funnelNext ? funnelNext.href : "mailto:hello@example.com"}
                onClick={funnelNext ? funnelNext.onClick : undefined}
                className="mt-7 inline-block px-6 py-3 rounded-full bg-white text-[#0B0D17] font-semibold text-sm"
              >
                {s.button}{funnelNext ? ` →` : ""}
              </a>
            )}
          </div>
        </section>
      );
    case "faq":
      return (
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-center">FAQs</h2>
            <div className="mt-10 space-y-3">
              {arr(s.items).map((it, i) => (
                <details key={i} className="vp-card p-5">
                  <summary className="font-display font-semibold cursor-pointer">{it.q}</summary>
                  <p className="text-sm text-[#4A4D57] mt-2 leading-relaxed">{it.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      );
    case "about":
      return (
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <h2 data-inline-field="title" className="font-display font-black text-3xl sm:text-4xl tracking-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title || "About") }} />
            <p data-inline-field="body" className="text-[#4A4D57] text-lg mt-5 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.body) }} />
          </div>
        </section>
      );
    case "pricing":
      return (
        <section className="py-16 sm:py-20 vp-hero-bg">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-center leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-4xl mx-auto">
              {arr(s.plans).map((p, i) => (
                <div key={i} className={"vp-glass p-5 sm:p-6 " + (p.featured ? "border-[#0B5CFF] shadow-[0_18px_44px_rgba(11,92,255,0.18)]" : "")}>
                  <div className="font-display font-bold text-lg sm:text-xl">{p.name}</div>
                  <div className="font-display font-black text-3xl sm:text-4xl mt-2 sm:mt-3" style={{ color: p.featured ? accent : undefined }}>{p.price}</div>
                  <ul className="mt-4 sm:mt-5 space-y-2 text-sm">
                    {arr(p.perks).map((k, j) => {
                      const isDivider = /^─+$/.test(String(k).trim());
                      if (isDivider) return <li key={j} className="border-t border-[#E4E6EC] my-2" aria-hidden="true"></li>;
                      return (
                        <li key={j} className="flex gap-2 items-start text-[#0B0D17]">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accent }} strokeWidth={2} />
                          <span>{k}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    case "gallery":
      return (
        <section className="py-16">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-4">
            {arr(s.images).map((src, i) => (
              <img key={i} src={src} alt={"g" + i} className="w-full aspect-[4/5] object-cover rounded-2xl" loading="lazy" />
            ))}
          </div>
        </section>
      );
    case "waitlist":
      return <WaitlistFormSection section={s} accent={accent} />;
    case "registration_form":
      return <RegistrationFormSection section={s} accent={accent} />;
    case "countdown":
      return <CountdownSection section={s} accent={accent} />;

    // -------- NEW SECTION KINDS --------
    case "youtube_video": {
      const url = String(s.url || "");
      // Extract YT id from various URL formats
      const m = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/);
      const vid = m ? m[1] : "";
      return (
        <section className="py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-center mb-8 leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            {vid ? (
              <div className="aspect-video rounded-2xl overflow-hidden vp-glass p-1.5">
                <iframe
                  src={`https://www.youtube.com/embed/${vid}`}
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full rounded-xl"
                />
              </div>
            ) : (
              <div className="vp-card p-8 text-center text-[#747680] text-sm">Paste a YouTube URL to preview the video.</div>
            )}
          </div>
        </section>
      );
    }
    case "youtube_grid": {
      const items = arr(s.items);
      const extractId = (u) => {
        const m = String(u || "").match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/);
        return m ? m[1] : "";
      };
      return (
        <section className="py-16 sm:py-20 bg-[#F6F7FA] border-y border-[#E4E6EC]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-center leading-tight vp-fade-up" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            {s.subtitle && <p data-inline-field="subtitle" className="text-center text-[#4A4D57] mt-3 max-w-2xl mx-auto vp-fade-up" style={{ animationDelay: "80ms" }} dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.subtitle) }} />}
            <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {items.map((it, i) => {
                const vid = extractId(it.url);
                const thumb = vid ? `https://i.ytimg.com/vi/${vid}/hqdefault.jpg` : null;
                return (
                  <a key={i} href={it.url} target="_blank" rel="noreferrer"
                     className="group vp-card overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl block vp-fade-up"
                     style={{ animationDelay: `${Math.min(i * 60, 480)}ms` }}>
                    <div className="relative aspect-video overflow-hidden bg-black">
                      {thumb && (
                        <img src={thumb} alt={it.title || `Podcast ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/10 transition-colors">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform" style={{ background: accent }}>
                          <svg className="w-5 h-5 text-white ml-1" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      </div>
                    </div>
                    {it.title && (
                      <div className="p-4">
                        <div className="font-display font-semibold text-sm text-[#0B0D17] line-clamp-2 leading-snug">{it.title}</div>
                        {it.channel && <div className="text-xs text-[#747680] mt-1.5">{it.channel}</div>}
                      </div>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      );
    }
    case "video_embed": {
      const src = s.url || "";
      const isPortrait = s.orientation === "portrait" || s.portrait === true;
      return (
        <section className="py-16 sm:py-20">
          <div className={"mx-auto px-5 sm:px-8 " + (isPortrait ? "max-w-[420px]" : "max-w-3xl")}>
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-center mb-8 leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            {src ? (
              <video
                src={src}
                controls
                playsInline
                className={"w-full rounded-2xl vp-glass p-1.5 " + (isPortrait ? "aspect-[9/16] object-cover bg-black" : "")}
                poster={s.poster || undefined}
              />
            ) : (
              <div className="vp-card p-8 text-center text-[#747680] text-sm">Paste an MP4 URL to embed a video.</div>
            )}
          </div>
        </section>
      );
    }
    case "instagram_embed":
      return (
        <section className="py-16 sm:py-20 bg-gradient-to-br from-[#FDF2F8] to-[#FEF3C7]">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F472B6] via-[#F97316] to-[#FBBF24] shadow-lg mb-5">
              <Instagram className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            {s.handle && <div className="text-[#4A4D57] mt-2 text-lg font-medium">{s.handle}</div>}
            {s.url && (
              <a href={s.url} target="_blank" rel="noreferrer noopener"
                 className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full text-white font-semibold text-sm"
                 style={{ background: "linear-gradient(135deg,#833AB4,#FD1D1D,#FCB045)" }}>
                <Instagram className="w-4 h-4" /> Follow on Instagram
              </a>
            )}
          </div>
        </section>
      );
    case "marquee": {
      const text = s.text || "";
      return (
        <section className="py-4 overflow-hidden" style={{ background: accent }}>
          <div className="flex whitespace-nowrap vp-ticker">
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="inline-flex items-center gap-6 px-6 font-display font-black text-white text-lg sm:text-xl tracking-tight uppercase">
                {text}
                <span className="opacity-60">✦</span>
              </span>
            ))}
          </div>
        </section>
      );
    }
    case "button":
      return (
        <section className="py-10 sm:py-12">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
            {s.title && <h3 data-inline-field="title" className="font-display font-bold text-lg sm:text-xl mb-4 text-[#0B0D17]" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            <a
              href={s.url || (funnelNext ? funnelNext.href : "#cta")}
              onClick={funnelNext && !s.url ? funnelNext.onClick : undefined}
              target={s.new_tab && s.url ? "_blank" : undefined}
              rel={s.new_tab && s.url ? "noreferrer noopener" : undefined}
              className={"inline-flex items-center gap-2 font-semibold text-sm sm:text-base transition-all " +
                (s.style === "outline"
                  ? "px-6 py-3 rounded-full border-2 hover:opacity-80"
                  : "px-6 py-3 rounded-full text-white shadow-md hover:shadow-lg hover:opacity-90")}
              style={s.style === "outline"
                ? { color: accent, borderColor: accent }
                : { background: accent }}
            >
              {s.label || "Click here"} <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      );
    case "logos_strip":
      return (
        <section className="py-10 sm:py-12 border-y border-[#E4E6EC] bg-[#F6F7FA]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            {s.title && <div data-inline-field="title" className="text-center text-[#747680] text-xs uppercase tracking-widest font-semibold mb-4" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            <div className="flex items-center justify-around flex-wrap gap-6 opacity-80">
              {arr(s.items).map((name, i) => (
                <div key={i} className="font-display font-black text-lg sm:text-xl text-[#4A4D57] tracking-tight">{name}</div>
              ))}
            </div>
          </div>
        </section>
      );
    case "contact_info": {
      const iconFor = (kind) => {
        if (kind === "whatsapp") return MessageCircle;
        if (kind === "phone") return Phone;
        if (kind === "email") return Mail;
        if (kind === "address") return MapPin;
        return MessageCircle;
      };
      const hrefFor = (it) => {
        if (it.kind === "whatsapp") return `https://wa.me/${String(it.value || "").replace(/[^0-9]/g, "")}`;
        if (it.kind === "phone") return `tel:${it.value}`;
        if (it.kind === "email") return `mailto:${it.value}`;
        return undefined;
      };
      return (
        <section className="py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-center mb-8 leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {arr(s.items).map((it, i) => {
                const Icon = iconFor(it.kind);
                const href = hrefFor(it);
                const inner = (
                  <div className="vp-card p-4 sm:p-5 flex items-center gap-3 hover:border-[color:var(--vp-accent,#0B5CFF)] transition-colors">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: accent + "12", color: accent }}>
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-[#747680] font-semibold">{it.kind || "contact"}</div>
                      <div className="text-sm sm:text-base font-medium text-[#0B0D17] break-words">{it.value}</div>
                    </div>
                  </div>
                );
                return href ? (
                  <a key={i} href={href} target={it.kind === "whatsapp" ? "_blank" : undefined} rel="noreferrer">{inner}</a>
                ) : (
                  <div key={i}>{inner}</div>
                );
              })}
            </div>
          </div>
        </section>
      );
    }
    case "steps":
      return (
        <section className="py-16 sm:py-20 bg-[#F6F7FA] border-y border-[#E4E6EC]">
          <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight text-center mb-10 leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
              {arr(s.items).map((it, i) => (
                <div key={i} className="vp-card p-5 sm:p-6 relative">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-display font-black text-xl mb-4" style={{ background: accent, color: "white" }}>
                    {it.n || (i + 1)}
                  </div>
                  <div className="font-display font-bold text-lg sm:text-xl leading-snug">{it.t}</div>
                  <p className="text-sm text-[#4A4D57] mt-2 leading-relaxed">{it.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    case "image_text":
      return (
        <section className="py-16 sm:py-20">
          <div className={"max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center " + (s.reverse ? "lg:[direction:rtl]" : "")}>
            {s.image && (
              <div className="[direction:ltr]">
                <img src={s.image} alt={s.title || "image"} className="w-full max-h-[500px] object-contain rounded-2xl vp-glass p-3 bg-white" loading="lazy" />
              </div>
            )}
            <div className="[direction:ltr]">
              {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
              {s.body && <p data-inline-field="body" className="text-[#4A4D57] text-base sm:text-lg mt-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.body) }} />}
              {s.cta && (
                <a href={s.cta_url || "#cta"} className="mt-6 inline-flex items-center gap-2 vp-btn-primary text-sm" style={{ background: accent, borderColor: accent }}>
                  {s.cta} <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </section>
      );
    case "text_block":
      return (
        <section className="py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            {s.title && <h2 data-inline-field="title" className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight leading-tight" dangerouslySetInnerHTML={{ __html: renderableRichHtml(s.title) }} />}
            {s.body && (
              <div className="text-[#4A4D57] text-base sm:text-lg mt-5 leading-relaxed whitespace-pre-wrap">
                {s.body}
              </div>
            )}
          </div>
        </section>
      );
    case "custom_code":
      return <CustomCodeSection section={s} />;

    default:
      return (
        <section className="py-8">
          <div className="max-w-3xl mx-auto px-5 text-[#747680] text-sm">Unsupported section: {s.kind}</div>
        </section>
      );
  }
}
